import React, { useState, useEffect } from 'react';
import { useApp } from '../../../context/AppContext';
import { Card, CardHeader, CardTitle, Btn, Badge, SectionHead, Modal, FormGroup, FormGrid, Input, CustomSelect, Textarea, InfoBanner, DataField } from '../../common/UI';
import { generateDeliveryNotePDF } from '../../../utils/pdfGenerator';

export default function DeliveryOrders({ onNav }) {
  const { dns, stock, activeStock, addDN, updateDN, dispatchDN, completeDN, genDNNumber, showToast, role } = useApp();
  const [showCreate, setShowCreate] = useState(false);
  const [editTargetDN, setEditTargetDN] = useState(null);
  const [form, setForm] = useState({
    packages: '', totalWeight: '', pkgType: 'PKG', delType: 'port', dims: '', deliveredTo: '', destination: '',
    fixedRemarks: '', internalRemarks: '', selectedItems: [], deliveryDate: new Date().toISOString().split('T')[0],
    dimList: [{ length: '', width: '', height: '' }],
    doc_type: 'Delivery Note'
  });
  const [editForm, setEditForm] = useState({});
  const [poSearch, setPoSearch] = useState('');
  const [search, setSearch] = useState('');
  const [editRemarks, setEditRemarks] = useState({});

  const handleEditDN = async () => {
    if (!editForm.manual_packages || !editForm.manual_total_weight || !editForm.destination || !editForm.delivered_to) {
      showToast('Fill all required fields', 'error'); return;
    }
    const itemIds = editForm.selectedItems;
    if (!itemIds || itemIds.length === 0) {
      showToast('At least one item must be selected', 'error'); return;
    }

    await updateDN(editTargetDN.id, {
      ...editForm,
      itemIds
    });
    setEditTargetDN(null);
  };
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completePhotos, setCompletePhotos] = useState([]);
  const [completeDocs, setCompleteDocs] = useState([]);
  const [signedBy, setSignedBy] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCompletePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCompletePhotos(p => [...p, {
          id: Date.now() + Math.random(),
          file: file,
          name: file.name,
          preview: ev.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeCompletePhoto = (id) => setCompletePhotos(p => p.filter(x => x.id !== id));

  const handleCompleteDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCompleteDocs(p => [...p, {
          id: Date.now() + Math.random(),
          file: file,
          name: file.name,
          size: (file.size / 1024).toFixed(1) + ' KB'
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeCompleteDoc = (id) => setCompleteDocs(p => p.filter(x => x.id !== id));

  const handleFinalComplete = async () => {
    if (!signedBy) { showToast('Receiver name is required', 'error'); return; }
    if (completePhotos.length === 0) { showToast('Upload confirmation photo', 'error'); return; }

    const fd = new FormData();
    fd.append('signed_by', signedBy);
    completePhotos.forEach(p => fd.append('photos', p.file));
    completeDocs.forEach(d => fd.append('photos', d.file));

    try {
      await completeDN(completeTarget.id, fd);
      setCompleteTarget(null);
      setCompletePhotos([]);
      setCompleteDocs([]);
      setSignedBy('');
    } catch (err) {
      // Error handled by absolute completeDN
    }
  };

  /* Auto-calculate total weight when items are selected (Create) */
  useEffect(() => {
    if (form.selectedItems.length > 0) {
      const totalWt = form.selectedItems.reduce((sum, id) => {
        const item = activeStock.find(s => s.id === id);
        return sum + (item ? +item.weight : 0);
      }, 0);
      set('totalWeight', totalWt.toFixed(2));
    }
  }, [form.selectedItems, activeStock]);

  /* Auto-set package type from first selected stock item (Create) — user can still override */
  useEffect(() => {
    if (form.selectedItems.length > 0) {
      const firstItem = activeStock.find(s => s.id === form.selectedItems[0]);
      if (firstItem?.pkgType) set('pkgType', firstItem.pkgType);
    }
  }, [form.selectedItems, activeStock]);

  /* Auto-calculate total weight & packages when items are selected (Edit) */
  useEffect(() => {
    if (editTargetDN && editForm.selectedItems) {
      const totalWt = editForm.selectedItems.reduce((sum, id) => {
        const item = stock.find(s => s.id === id); // Look in all stock since it might be completed now
        return sum + (item ? +item.weight : 0);
      }, 0);
      const totalQty = editForm.selectedItems.reduce((sum, id) => {
        const item = stock.find(s => s.id === id);
        return sum + (item ? +item.quantity : 0);
      }, 0);

      setEditForm(p => ({
        ...p,
        manual_total_weight: totalWt.toFixed(2),
        manual_packages: p.manual_packages || totalQty // Only auto-set if empty or we want it dynamic? User said "not changing", suggesting they want it to update.
      }));
    }
  }, [editForm.selectedItems, stock, editTargetDN]);


  const handleCreate = () => {
    if (!form.packages || !form.totalWeight || !form.destination || !form.deliveredTo) {
      showToast('Fill all required fields', 'error'); return;
    }
    const customer = form.selectedItems.length > 0 ? (activeStock.find(s => s.id === form.selectedItems[0])?.customer || '') : '';
    const vessel = form.selectedItems.length > 0 ? (activeStock.find(s => s.id === form.selectedItems[0])?.vessel || '') : '';
    const finalDims = form.dimList.map(d => `${d.length || 0}X${d.width || 0}X${d.height || 0}`).join(', ');
    addDN({
      manual_packages: +form.packages,
      manual_total_weight: +form.totalWeight,
      package_type: form.pkgType,
      delivery_type: form.delType,
      manual_dimensions: finalDims,
      delivered_to: form.deliveredTo,
      destination: form.destination,
      delivery_date: form.deliveryDate,
      fixed_remarks: form.fixedRemarks,
      internal_remarks: form.internalRemarks,
      customer, vessel, createdBy: 'Budi Wibowo',
      itemIds: form.selectedItems,
      doc_type: form.doc_type
    });
    setShowCreate(false);
    setForm({
      packages: '', totalWeight: '', pkgType: 'PKG', delType: 'port', dims: '', deliveredTo: '', destination: '',
      fixedRemarks: '', internalRemarks: '', selectedItems: [], deliveryDate: new Date().toISOString().split('T')[0],
      dimList: [{ length: '', width: '', height: '' }],
      doc_type: 'Delivery Note'
    });
    setPoSearch('');
  };

  const genPDF = (dn) => {
    // FORCE doc_type if missing - explicitly check both
    const dt = dn.doc_type || dn.docType || 'Delivery Note';
    const docData = { ...dn, doc_type: dt };
    generateDeliveryNotePDF(docData, dn.items);
    showToast(`📄 PDF: ${dt}`, 'success');
  };

  const dnNumber = genDNNumber();

  // Filter DN list by Vessel name, DN number, or Customer name
  const filteredDNs = dns.filter(dn => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (dn.vessel || '').toLowerCase().includes(q) ||
           (dn.dnNumber || dn.dn_number || '').toLowerCase().includes(q) ||
           (dn.customer || '').toLowerCase().includes(q);
  });

  useEffect(() => { setCurrentPage(1); }, [search]);

  const totalPages = Math.ceil(filteredDNs.length / itemsPerPage);
  const currentItems = filteredDNs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="fade-up page-root">
      <SectionHead title="Delivery Orders" subtitle="Manage delivery notes and generate PDFs">
        <Btn variant="primary" onClick={() => setShowCreate(true)}>
          <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Delivery Order
        </Btn>
      </SectionHead>

      {dns.length > 0 && (
        <div style={{ marginBottom: 16, position: 'relative', maxWidth: 360 }}>
          <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
            width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by Customer, Vessel name, or DN number..."
            style={{ width: '100%', background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '9px 12px 9px 36px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' }} />
        </div>
      )}

      {dns.length > 0 && filteredDNs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', fontSize: 14 }}>
          🔍 No delivery orders match “<b>{search}</b>”.
        </div>
      )}

      {dns.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚚</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)', marginBottom: 6 }}>No Delivery Orders Yet</div>
          <div style={{ fontSize: 13, marginBottom: 16 }}>Select active stock items to create a delivery order.</div>
          <Btn variant="primary" onClick={() => onNav('stock-list')}>Go to Stock List</Btn>
        </div>
      )}

      {currentItems.map(dn => (
        <DNCard key={dn.id} dn={dn} role={role}
          editRemark={editRemarks[dn.id]}
          onEditRemark={val => setEditRemarks(p => ({ ...p, [dn.id]: val }))}
          onSaveRemark={() => { updateDN(dn.id, { internalRemarks: editRemarks[dn.id] }); setEditRemarks(p => { const n = { ...p }; delete n[dn.id]; return n; }); }}
          onCancelRemark={() => setEditRemarks(p => { const n = { ...p }; delete n[dn.id]; return n; })}
          onStartEditRemark={() => setEditRemarks(p => ({ ...p, [dn.id]: dn.internalRemarks || dn.remarks || '' }))}
          onDispatch={() => dispatchDN(dn.id)}
          onComplete={() => setCompleteTarget(dn)}
          onPDF={() => genPDF(dn)}
          onEdit={() => {
            setEditTargetDN(dn);
            setEditForm({
              manual_packages: dn.manual_packages || dn.packages || '',
              manual_total_weight: dn.manual_total_weight || dn.totalWeight || '',
              package_type: dn.package_type || 'PKG',
              delivery_type: dn.delivery_type || 'port',
              manual_dimensions: dn.manual_dimensions || dn.dimensions || '',
              delivered_to: dn.delivered_to || '',
              destination: dn.destination || '',
              delivery_date: dn.delivery_date || dn.createdDate || '',
              fixed_remarks: dn.fixed_remarks || dn.remarks || '',
              internal_remarks: dn.internal_remarks || '',
              doc_type: dn.doc_type || 'Delivery Note',
              selectedItems: dn.items?.map(i => i.id) || []
            });
          }}
        />
      ))}
      
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '16px 0', marginTop: 8 }}>
          <Btn variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>← Prev</Btn>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>
            Page {currentPage} of {totalPages}
          </div>
          <Btn variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next →</Btn>
        </div>
      )}

      {/* Complete Modal */}
      <Modal open={!!completeTarget} onClose={() => { setCompleteTarget(null); setCompletePhotos([]); setCompleteDocs([]); setSignedBy(''); }}
        title="Mark DN as Completed" icon="✅"
        footer={<>
          <Btn variant="outline" onClick={() => { setCompleteTarget(null); setCompletePhotos([]); setCompleteDocs([]); setSignedBy(''); }}>Cancel</Btn>
          <Btn variant="success" onClick={handleFinalComplete}>✓ Confirm Completion</Btn>
        </>}>
        <InfoBanner>📷 Upload final delivery photos and signature confirmation.</InfoBanner>
        <FormGroup label="Photo Evidence" required>
          <input type="file" id="compPhoto" accept="image/*" multiple style={{ display: 'none' }} onChange={handleCompletePhotoUpload} />
          {completePhotos.length === 0 ? (
            <label htmlFor="compPhoto" style={{ display: 'block', border: '2px dashed var(--border)', padding: 30, textAlign: 'center', cursor: 'pointer', borderRadius: 10 }}>
              <div style={{ fontSize: 24 }}>📷</div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>Click to upload completion photos</div>
            </label>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {completePhotos.map(p => (
                <div key={p.id} style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: '1.5px solid var(--border)', position: 'relative' }}>
                  <img src={p.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div onClick={() => removeCompletePhoto(p.id)} style={{ position: 'absolute', top: 0, right: 0, padding: '2px 5px', background: 'var(--danger)', color: '#fff', fontSize: 10, cursor: 'pointer' }}>✕</div>
                </div>
              ))}
            </div>
          )}
        </FormGroup>

        {/* Document Proof Section Below Photo Evidence */}
        <div style={{ marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            📄 Document Proof (Optional) <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>PDF, Word, Excel etc.</span>
          </div>
          <input type="file" id="compDocInput" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple
            style={{ display: 'none' }} onChange={handleCompleteDocUpload} />
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {completeDocs.map(d => (
              <div key={d.id} style={{
                padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8,
                border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10,
                position: 'relative'
              }}>
                <span style={{ fontSize: 18 }}>📄</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                  <div style={{ fontSize: 9, color: 'var(--muted)' }}>{d.size}</div>
                </div>
                <div onClick={() => removeCompleteDoc(d.id)} style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 800, fontSize: 14, padding: '0 4px' }}>✕</div>
              </div>
            ))}
            <label htmlFor="compDocInput" style={{
              padding: '10px 16px', borderRadius: 8, border: '2px dashed var(--border2)',
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600,
              color: 'var(--muted)', cursor: 'pointer', transition: 'all .2s'
            }}>
              <span>+ Add Document Proof</span>
            </label>
          </div>
        </div>
        <FormGroup label="Signed By" required>
          <Input value={signedBy} onChange={e => setSignedBy(e.target.value)} placeholder="Capt. Name or Receiver Name" />
        </FormGroup>
      </Modal>

      {/* Edit DN Modal */}
      <Modal open={!!editTargetDN} onClose={() => setEditTargetDN(null)}
        title={`Edit ${editForm.doc_type || 'Delivery Note'}`}
        subtitle={`Editing ${editTargetDN?.dnNumber}`}
        icon="✏️" wide
        footer={<>
          <div style={{ marginRight: 'auto', display: 'flex', gap: 12, alignItems: 'center', background: 'var(--surface2)', padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>PDF Type:</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={editForm.doc_type === 'Delivery Note'} onChange={() => setEditForm(p => ({...p, doc_type: 'Delivery Note'}))} /> Delivery Note
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={editForm.doc_type === 'Release Note'} onChange={() => setEditForm(p => ({...p, doc_type: 'Release Note'}))} /> Release Note
            </label>
          </div>
          <Btn variant="outline" onClick={() => setEditTargetDN(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleEditDN}>Save Changes</Btn>
        </>}>

        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: 24, marginBottom: 24 }}>
          {/* Active Stock Selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                1. Inventory Select
              </div>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
                  width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <Input value={poSearch} onChange={e => setPoSearch(e.target.value)}
                  placeholder="Search PO or Vessel..."
                  style={{ paddingLeft: 32, fontSize: 12 }} />
              </div>
            </div>

            <div style={{
              border: '1.5px solid var(--border)',
              borderRadius: 12,
              overflow: 'hidden',
              height: 420,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface2)',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {activeStock.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>No active stock.</div>
                ) : (
                  [...activeStock]
                    .filter(s => !poSearch || (s.po || '').toLowerCase().includes(poSearch.toLowerCase()) || (s.vessel || '').toLowerCase().includes(poSearch.toLowerCase()))
                    .map(s => (
                    <label key={s.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                        borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        background: editForm.selectedItems?.includes(s.id) ? 'var(--accent-light)' : 'transparent'
                      }}>
                      <input type="checkbox" checked={editForm.selectedItems?.includes(s.id)}
                        onChange={() => setEditForm(p => ({
                          ...p,
                          selectedItems: p.selectedItems.includes(s.id) ? p.selectedItems.filter(x => x !== s.id) : [...p.selectedItems, s.id]
                        }))} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 13 }}>{s.po}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.vessel} · {s.qty} {s.pkgType}</div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Selected Preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              2. Selected Items ({editForm.selectedItems?.length})
            </div>
            <div style={{ border: '1.5px solid var(--border)', borderRadius: 12, height: 420, overflowY: 'auto', background: '#fff' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10, borderBottom: '1px solid var(--border)' }}>
                  <tr>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: 11 }}>Item Detail</th>
                    <th style={{ padding: 12, textAlign: 'right', fontSize: 11 }}>Qty/Wgt</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editForm.selectedItems?.map(id => {
                    const s = stock.find(x => x.id === id);
                    if (!s) return null;
                    return (
                      <tr key={id} style={{ borderBottom: '1px solid var(--surface2)' }}>
                        <td style={{ padding: 12 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{s.po}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.description}</div>
                        </td>
                        <td style={{ padding: 12, textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{s.qty} / {s.weight}</div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button onClick={() => setEditForm(p => ({...p, selectedItems: p.selectedItems.filter(x => x !== id)}))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger)' }}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <FormGrid>
          <FormGroup label="Delivery Date" required>
            <Input type="date" value={editForm.delivery_date} onChange={e => setEditForm(p => ({...p, delivery_date: e.target.value}))} />
          </FormGroup>
          <FormGroup label="Delivered To" required>
            <div style={{ display: 'flex', gap: 10 }}>
              <Input value={editForm.delivered_to} onChange={e => setEditForm(p => ({...p, delivered_to: e.target.value}))} style={{ flex: 1 }} />
              <CustomSelect value={editForm.delivery_type} onChange={e => setEditForm(p => ({...p, delivery_type: e.target.value}))} style={{ width: 140 }}>
                <option value="export">Export</option>
                <option value="warehouse">Warehouse</option>
                <option value="vessel">Vessel</option>
                <option value="port">Port</option>
              </CustomSelect>
            </div>
          </FormGroup>
          <FormGroup label="Destination" required>
            <Input value={editForm.destination} onChange={e => setEditForm(p => ({...p, destination: e.target.value}))} />
          </FormGroup>
          <FormGroup label="Packages & Weight" required>
            <div style={{ display: 'flex', gap: 10 }}>
              <Input type="number" value={editForm.manual_packages} onChange={e => setEditForm(p => ({...p, manual_packages: e.target.value}))} placeholder="Pkgs" />
              <CustomSelect value={editForm.package_type} onChange={e => setEditForm(p => ({...p, package_type: e.target.value}))} style={{ width: 120 }}>
                <option value="PKG">PKG</option><option value="CTN">CTN</option><option value="PLT">PLT</option><option value="DRM">DRM</option><option value="BOX">BOX</option><option value="BAG">BAG</option>
              </CustomSelect>
              <Input value={editForm.manual_total_weight} onChange={e => setEditForm(p => ({...p, manual_total_weight: e.target.value}))} placeholder="Total Wgt" />
            </div>
          </FormGroup>
          <FormGroup label="Dimensions" full>
            <Input value={editForm.manual_dimensions} onChange={e => setEditForm(p => ({...p, manual_dimensions: e.target.value}))} placeholder="100x100x100, 200x200x200" />
          </FormGroup>
          <FormGroup label="Fulfillment Remarks" full>
            <Textarea value={editForm.fixed_remarks} onChange={e => setEditForm(p => ({...p, fixed_remarks: e.target.value}))} />
          </FormGroup>
        </FormGrid>
      </Modal>

      {/* Create DN Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)}
        title="Create Delivery Order"
        subtitle={`New ${form.doc_type || 'Delivery Note'}`}
        icon="🚚" wide
        footer={<>
          <div style={{ marginRight: 'auto', display: 'flex', gap: 12, alignItems: 'center', background: 'var(--surface2)', padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase' }}>PDF Type:</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={form.doc_type === 'Delivery Note'} onChange={() => set('doc_type', 'Delivery Note')} /> Delivery Note
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={form.doc_type === 'Release Note'} onChange={() => set('doc_type', 'Release Note')} /> Release Note
            </label>
          </div>
          <Btn variant="outline" onClick={() => setShowCreate(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleCreate}>Create Delivery Order</Btn>
        </>}>

        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: 24, marginBottom: 24 }}>
          {/* Active Stock Selector */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                1. Inventory Select
              </div>
            <div style={{ position: 'relative' }}>
              <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', pointerEvents: 'none' }}
                width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <Input value={poSearch} onChange={e => setPoSearch(e.target.value)}
                placeholder="Search PO or Vessel..."
                style={{ paddingLeft: 32, fontSize: 12 }} />
            </div>
          </div>

            <div style={{
              border: '1.5px solid var(--border)',
              borderRadius: 12,
              overflow: 'hidden',
              height: 420,
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface2)',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {activeStock.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}>📦</div>
                    No active stock available.
                  </div>
                ) : (
                  [...activeStock]
                    .filter(s => !poSearch || (s.po || '').toLowerCase().includes(poSearch.toLowerCase()) || (s.vessel || '').toLowerCase().includes(poSearch.toLowerCase()))
                    .sort((a, b) => b.id - a.id)
                    .map(s => (
                    <label key={s.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                        borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        background: form.selectedItems.includes(s.id) ? 'var(--accent-light)' : 'transparent',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        userSelect: 'none'
                      }}
                      onMouseEnter={e => { if (!form.selectedItems.includes(s.id)) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                      onMouseLeave={e => { if (!form.selectedItems.includes(s.id)) e.currentTarget.style.background = 'transparent'; }}>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <input type="checkbox" checked={form.selectedItems.includes(s.id)}
                          onChange={() => set('selectedItems', form.selectedItems.includes(s.id)
                            ? form.selectedItems.filter(x => x !== s.id)
                            : [...form.selectedItems, s.id])}
                          style={{
                            width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--accent)',
                            borderRadius: 4, border: '2px solid var(--border2)'
                          }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, color: 'var(--text)', fontSize: 13, marginBottom: 2 }}>{s.po}</div>
                        <div style={{ color: 'var(--muted)', fontSize: 11, display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.vessel}</span>
                          <span style={{ color: 'var(--border2)' }}>|</span>
                          <span>{s.qty} {s.pkgType}</span>
                          <span style={{ color: 'var(--border2)' }}>|</span>
                          <span>{s.weight} {s.unit}</span>
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Selected Preview - Modern Grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                2. Selected for Delivery ({form.selectedItems.length})
              </div>
              {form.selectedItems.length > 0 && (
                <Btn variant="ghost" size="xs" onClick={() => set('selectedItems', [])} style={{ color: 'var(--danger)', fontSize: 11 }}>Clear All</Btn>
              )}
            </div>

            <div style={{
              border: '1.5px solid var(--border)',
              borderRadius: 12,
              overflow: 'hidden',
              height: 420,
              background: 'var(--white)',
              boxShadow: 'var(--shadow-md)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {form.selectedItems.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', padding: 40 }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>📝</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text2)', marginBottom: 4 }}>Ready to Deliver</div>
                  <div style={{ fontSize: 12, textAlign: 'center' }}>Select items from the list on the left to include them in this Delivery Note.</div>
                </div>
              ) : (
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10, borderBottom: '2px solid var(--border)' }}>
                      <tr>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase' }}>Item Detail</th>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', textAlign: 'center' }}>Qty</th>
                        <th style={{ padding: '14px 16px', fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', textAlign: 'right' }}>Weight</th>
                        <th style={{ padding: '14px 16px', width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.selectedItems.map((id, idx) => {
                        const s = activeStock.find(x => x.id === id);
                        if (!s) return null;
                        return (
                          <tr key={id} style={{
                            borderBottom: '1px solid var(--surface2)',
                            transition: 'background 0.1s',
                            background: idx % 2 === 0 ? 'var(--white)' : 'var(--surface-hover)'
                          }}>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 13 }}>{s.po}</div>
                              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{s.description || 'No description'}</div>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <div style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                background: 'var(--accent-light)',
                                color: 'var(--accent)',
                                borderRadius: 6,
                                fontWeight: 800,
                                fontSize: 12
                              }}>
                                {s.qty} <span style={{ fontSize: 10, fontWeight: 600 }}>{s.pkgType}</span>
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              <div style={{ fontWeight: 700, color: 'var(--text2)', fontSize: 13 }}>{s.weight}</div>
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{s.unit}</div>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                              <button
                                onClick={() => set('selectedItems', form.selectedItems.filter(x => x !== id))}
                                style={{
                                  border: 'none', background: 'none', color: 'var(--muted)', cursor: 'pointer',
                                  padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'var(--danger-bg)'; }}
                                onMouseLeave={e => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.background = 'none'; }}>
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {form.selectedItems.length > 0 && (
                <div style={{ padding: '16px 20px', background: 'var(--surface2)', borderTop: '2px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 2 }}>Total Items</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{form.selectedItems.length}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 2 }}>Gross Weight</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent)' }}>{form.totalWeight} <span style={{ fontSize: 12 }}>kg</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
          3. Delivery & Fulfillment Details
        </div>
        <Card style={{ padding: 20, background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 12 }}>
          <FormGrid>
            <FormGroup label="Delivery Date" required>
              <Input type="date" value={form.deliveryDate} min={new Date().toISOString().split('T')[0]} onChange={e => set('deliveryDate', e.target.value)} />
            </FormGroup>
            <FormGroup label="Delivered To" required hint="Person or company receiving items">
              <div style={{ display: 'flex', gap: 10 }}>
                <Input value={form.deliveredTo} onChange={e => set('deliveredTo', e.target.value)} placeholder="Recipient Name / Vessel Agent" style={{ flex: 1 }} />
                <CustomSelect value={form.delType} onChange={e => set('delType', e.target.value)} style={{ width: 140 }}>
                  <option value="export">Export</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="vessel">Vessel</option>
                  <option value="port">Port</option>
                </CustomSelect>
              </div>
            </FormGroup>
            <FormGroup label="Destination" required>
              <Input value={form.destination} onChange={e => set('destination', e.target.value)} placeholder="Port / Warehouse Location" />
            </FormGroup>
            <FormGroup label="Final Package Count & Weight" required hint="Enter final packed details">
              <div style={{ display: 'flex', gap: 10 }}>
                <Input type="number" min="1" value={form.packages} onChange={e => {
                  const newQty = parseInt(e.target.value) || 0;
                  set('packages', e.target.value);
                  setForm(p => {
                    const newList = [...p.dimList];
                    if (newQty > newList.length && newQty <= 100) {
                      while(newList.length < newQty) newList.push({ length: '', width: '', height: '' });
                    } else if (newQty < newList.length && newQty > 0) {
                      newList.length = newQty;
                    }
                    return { ...p, dimList: newList };
                  });
                }} placeholder="Total Pkgs" />
                <CustomSelect value={form.pkgType} onChange={e => set('pkgType', e.target.value)} style={{ width: 120 }}>
                  <option value="PKG">PKG</option>
                  <option value="CTN">CTN</option>
                  <option value="PLT">PLT</option>
                  <option value="DRM">DRM</option>
                  <option value="BOX">BOX</option>
                  <option value="BAG">BAG</option>
                </CustomSelect>
                <Input value={form.totalWeight} onChange={e => set('totalWeight', e.target.value)} placeholder="Total Wgt" />
                <div style={{ alignSelf: 'center', fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>KG</div>
              </div>
            </FormGroup>
              <div style={{ gridColumn: '1 / -1', background: 'var(--surface2)', padding: 16, borderRadius: 12, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Dimensions for {form.dimList.length} Item(s)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {form.dimList.map((dim, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                      <FormGroup label={`#${idx+1} Length (cm)`}>
                        <Input type="number" value={dim.length} onChange={e => {
                          const nl = [...form.dimList]; nl[idx].length = e.target.value; set('dimList', nl);
                        }} placeholder="0" />
                      </FormGroup>
                      <FormGroup label={`#${idx+1} Width (cm)`}>
                        <Input type="number" value={dim.width} onChange={e => {
                          const nl = [...form.dimList]; nl[idx].width = e.target.value; set('dimList', nl);
                        }} placeholder="0" />
                      </FormGroup>
                      <FormGroup label={`#${idx+1} Height (cm)`}>
                        <Input type="number" value={dim.height} onChange={e => {
                          const nl = [...form.dimList]; nl[idx].height = e.target.value; set('dimList', nl);
                        }} placeholder="0" />
                      </FormGroup>
                    </div>
                  ))}
                </div>
              </div>
            <FormGroup label="Fulfillment Remarks (Printed on PDF)" full>
              <Textarea value={form.fixedRemarks} onChange={e => set('fixedRemarks', e.target.value)}
                placeholder="Enter special instructions or remarks that should appear on the document..."
                style={{ minHeight: 80 }} />
            </FormGroup>
          </FormGrid>
        </Card>
      </Modal>
    </div>
  );
}

function DNCard({ dn, role, onEdit, editRemark, onEditRemark, onSaveRemark, onCancelRemark, onStartEditRemark, onDispatch, onComplete, onPDF }) {
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--white)',
      boxShadow: 'var(--shadow)', marginBottom: 16, overflow: 'hidden'
    }}>

      {/* Head */}
      <div className="dn-card-head" style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 17, fontWeight: 600, color: 'var(--accent)', letterSpacing: '.5px' }}>{dn.dnNumber}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>{dn.customer}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{dn.vessel} · Created {dn.createdDate} · by {dn.createdBy}</div>
        </div>
        <Badge status={dn.status} />
        {role === 'admin' && (
          <Btn size="sm" variant="outline" onClick={onEdit}>✏ Edit DN</Btn>
        )}
        {dn.status === 'draft' && (
          <Btn size="sm" variant="primary" onClick={onDispatch}>🚚 Dispatch</Btn>
        )}
        {dn.status === 'dispatched' && (
          <Btn size="sm" variant="success" onClick={onComplete}>✓ Mark Complete</Btn>
        )}
      </div>

      {/* Body */}
      <div className="dn-card-body" style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
        <DataField label="Packages" value={`${dn.manual_packages || dn.packages || 0} ${dn.package_type || 'pkgs'}`} />
        <DataField label="Total Weight" value={`${dn.manual_total_weight || dn.totalWeight || 0} kg`} />
        <DataField label="Delivery Date" value={dn.delivery_date || dn.createdDate} />
        <DataField label="Delivered To" value={dn.delivered_to} />
        <DataField label="Delivery Type" value={dn.delivery_type} accent />
        <DataField label="Destination" value={dn.destination} />
      </div>

      {(dn.manual_dimensions || dn.dimensions) && (dn.manual_dimensions || dn.dimensions) !== '—' && (
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10, letterSpacing: '.5px' }}>
            📏 Dimensions for {(dn.manual_dimensions || dn.dimensions).split(',').length} Item(s)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8 }}>
            {(dn.manual_dimensions || dn.dimensions).split(',').map((d, i) => {
              const parts = d.trim().split(/X|x/);
              return (
                <div key={i} style={{ 
                  padding: '8px 12px', background: 'var(--white)', borderRadius: 8, 
                  border: '1.5px solid var(--border)', fontSize: 11.5, fontFamily: "'JetBrains Mono',monospace",
                  display: 'flex', alignItems: 'center'
                }}>
                  <span style={{ fontWeight: 800, color: 'var(--accent)', marginRight: 10, fontSize: 10 }}>#{i+1}</span>
                  {parts.length === 3 ? (
                    <span>{parts[0]}<small>(L)</small> × {parts[1]}<small>(W)</small> × {parts[2]}<small>(H)</small> <span style={{fontSize:9, color:'var(--muted)'}}>cm</span></span>
                  ) : d.trim()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fixed Remarks (locked) */}
      {(dn.remarks || dn.fixedRemarks) && (
        <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            🔒 Fixed Remarks (locked)
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
            {dn.fixedRemarks || dn.remarks || <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>No fixed remarks.</span>}
          </div>
        </div>
      )}

      {/* Internal Remarks (editable) */}
      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          ✏️ Internal Remarks (editable)
        </div>
        {editRemark !== undefined ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <textarea value={editRemark} onChange={e => onEditRemark(e.target.value)}
              style={{
                flex: 1, background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8,
                padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', minHeight: 56, outline: 'none'
              }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <Btn size="xs" variant="primary" onClick={onSaveRemark}>Save</Btn>
              <Btn size="xs" variant="outline" onClick={onCancelRemark}>Cancel</Btn>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
              {dn.internalRemarks || ''}
            </div>
            <Btn size="xs" variant="outline" onClick={onStartEditRemark}>✏ Edit</Btn>
          </div>
        )}
      </div>

      {/* PDF Buttons */}
      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, background: 'var(--surface2)' }}>
        <Btn variant="primary" size="sm" style={{ marginLeft: 'auto' }} onClick={onPDF}>
          📥 {dn.doc_type || 'Delivery Note'} PDF (V3)
        </Btn>
      </div>
    </div>
  );
}
